import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ClientProvider } from "@/context/ClientContext";
import { AiJobsProvider } from "@/context/AiJobsContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import ToolsPage from "@/pages/ToolsPage";
import ToolDetail from "@/pages/ToolDetail";
import ComparePage from "@/pages/ComparePage";
import CriteriaPage from "@/pages/CriteriaPage";
import ProcessesPage from "@/pages/ProcessesPage";
import ProcessDetail from "@/pages/ProcessDetail";
import TeamPage from "@/pages/TeamPage";
import GapAnalysisPage from "@/pages/GapAnalysisPage";
import InviteRegister from "@/pages/InviteRegister";
import ReadinessPage from "@/pages/ReadinessPage";
import ReadinessDetail from "@/pages/ReadinessDetail";
import ReadinessCompare from "@/pages/ReadinessCompare";
import ClientsPage from "@/pages/ClientsPage";
import ClientDetail from "@/pages/ClientDetail";
import OnePager from "@/pages/OnePager";
import Questionnaire from "@/pages/Questionnaire";
import ClientPortal from "@/pages/ClientPortal";
import FrameworksPage from "@/pages/FrameworksPage";
import RemediationLibrary from "@/pages/RemediationLibrary";
import PipelinePage from "@/pages/PipelinePage";
import PlatformAdminPage from "@/pages/PlatformAdminPage";
import Landing from "@/pages/Landing";
import Wizard from "@/pages/Wizard";
import SubscriptionPage from "@/pages/SubscriptionPage";
import AssistantPage from "@/pages/AssistantPage";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PlatformRoutes() {
  return (
    <Routes>
      <Route path="/platform" element={<PlatformAdminPage />} />
      <Route path="*" element={<Navigate to="/platform" replace />} />
    </Routes>
  );
}

function AppRoutes() {
  const location = useLocation();
  const { user, loading, isPlatformAdmin } = useAuth();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  if (!loading && user && isPlatformAdmin) {
    return <PlatformRoutes />;
  }
  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/invite/:token" element={<InviteRegister />} />
      <Route path="/q/:token" element={<Questionnaire />} />
      <Route path="/portal" element={<ClientPortal />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><ClientsPage /></ProtectedRoute>} />
      <Route path="/sopralluogo" element={<ProtectedRoute><Wizard /></ProtectedRoute>} />
      <Route path="/clients/:id" element={<ProtectedRoute><ClientDetail /></ProtectedRoute>} />
      <Route path="/readiness" element={<ProtectedRoute><ReadinessPage /></ProtectedRoute>} />
      <Route path="/frameworks" element={<ProtectedRoute><FrameworksPage /></ProtectedRoute>} />
      <Route path="/remediation-library" element={<ProtectedRoute><RemediationLibrary /></ProtectedRoute>} />
      <Route path="/pipeline" element={<ProtectedRoute><PipelinePage /></ProtectedRoute>} />
      <Route path="/readiness/compare" element={<ProtectedRoute><ReadinessCompare /></ProtectedRoute>} />
      <Route path="/readiness/:id/onepager" element={<ProtectedRoute><OnePager /></ProtectedRoute>} />
      <Route path="/readiness/:id" element={<ProtectedRoute><ReadinessDetail /></ProtectedRoute>} />
      <Route path="/tools" element={<ProtectedRoute><ToolsPage /></ProtectedRoute>} />
      <Route path="/tools/:id" element={<ProtectedRoute><ToolDetail /></ProtectedRoute>} />
      <Route path="/compare" element={<ProtectedRoute><ComparePage /></ProtectedRoute>} />
      <Route path="/processes" element={<ProtectedRoute><ProcessesPage /></ProtectedRoute>} />
      <Route path="/processes/:id" element={<ProtectedRoute><ProcessDetail /></ProtectedRoute>} />
      <Route path="/gap-analysis" element={<ProtectedRoute><GapAnalysisPage /></ProtectedRoute>} />
      <Route path="/criteria" element={<ProtectedRoute><CriteriaPage /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
      <Route path="/abbonamento" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/assistente" element={<ProtectedRoute><AssistantPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ClientProvider>
            <AiJobsProvider>
              <AppRoutes />
              <Toaster position="top-right" />
            </AiJobsProvider>
          </ClientProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
